/**
 * useTierAdjustments: what the server had to change in the stored config to fit the user's plan,
 * from one /api/auth/me check when a screen opens. Reads never write back, so this stays non-empty
 * until the user saves; `clear` is for after a successful save.
 */

import { useCallback, useEffect, useState } from 'react';
import type { HttpClient } from '../api/httpClient';
import { adjustmentsFromBody, type ConfigAdjustment } from '../tierInputPolicy';

export interface UseTierAdjustmentsOptions {
  httpClient: HttpClient;
  enabled?: boolean;
}

export interface UseTierAdjustmentsResult {
  adjustments: ConfigAdjustment[];
  clear: () => void;
}

export function useTierAdjustments({
  httpClient,
  enabled = true,
}: UseTierAdjustmentsOptions): UseTierAdjustmentsResult {
  const [adjustments, setAdjustments] = useState<ConfigAdjustment[]>([]);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    httpClient
      .get<unknown>('/api/auth/me')
      .then((body) => {
        if (!cancelled) setAdjustments(adjustmentsFromBody(body));
      })
      .catch(() => {
        if (!cancelled) setAdjustments([]);
      });
    return () => {
      cancelled = true;
    };
  }, [httpClient, enabled]);

  const clear = useCallback(() => setAdjustments([]), []);
  return { adjustments, clear };
}
